// Sudoku interface over the server-rendered APG grid (UX guide §3.5): tap a square then a digit, or type;
// notes; no mistake limit; Check on demand; same-digit and conflict highlights; unlimited undo.
// Killer Sudoku (data-kind="killer") uses the same game with cages: their outlines and sums come from data-cages
// (sudoku/killer.ts), are drawn with CSS from attributes (so redrawing a square never wipes them), and a digit
// repeated inside a cage counts as a conflict.
import { BOX, COL, PEERS, ROW, UNITS } from "./core"
import { cageLayout, type Cage } from "./killer"
import { readJSON, writeJSON } from "../lib/store"
import { formatLong, isToday, mark } from "../lib/daily"
import { gameEvent } from "../lib/game-events"
import { gameProgress, newAttempt } from "../lib/stats"

export interface SudokuDaily { game: string; date: string }

interface Snapshot { entries: number[]; notes: number[]; attempt?: number }
interface Saved { v: 1; id: string; puzzle: string; solution: string; entries: number[]; notes: number[]; past: Snapshot[]; attempt?: number; cages?: Cage[] }

const bit = (d: number): number => 1 << (d - 1)
const noteList = (m: number): number[] => [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((d) => m & bit(d))

export class SudokuGame {
  private id: string
  private givens: number[] = []
  private solution: number[] = []
  private entries: number[] = []
  private notes: number[] = []
  private past: Snapshot[] = []
  private future: Snapshot[] = []
  private sel = 0
  private notesMode = false
  /** Squares Check marked, with the number that was wrong: the mark stays until that square changes. */
  private wrong = new Map<number, number>()
  /** Restarts of this puzzle; part of the puzzle id for the stats clock, and kept in every undo snapshot. */
  private attempt = 0
  private winSent = false
  private undos = 0
  private hints = 0
  private sentFirst = false
  private readonly cells: HTMLTableCellElement[]
  private readonly status = document.querySelector<HTMLElement>("[data-status]")
  private readonly level: string
  private readonly saveKey: string
  private readonly kind: "sudoku" | "killer"
  private cages: Cage[] = []
  private cageOf: number[] = []

  constructor(private readonly root: HTMLElement, private readonly daily?: SudokuDaily) {
    this.level = root.dataset.level ?? "easy"
    this.kind = root.dataset.kind === "killer" ? "killer" : "sudoku"
    this.saveKey = this.kind === "killer"
      ? (daily ? `dt:save:daily:killer-sudoku:${daily.date}:v1` : `dt:save:killer-${this.level}:v1`)
      : (daily ? `dt:save:daily:sudoku:${daily.date}:${this.level}:v1` : `dt:save:sudoku-${this.level}:v1`)
    this.cells = Array.from(root.querySelectorAll<HTMLTableCellElement>("td[data-i]"))
    this.id = root.dataset.id ?? ""
    const saved = readJSON<Saved>(this.saveKey)
    if (saved?.v === 1 && saved.puzzle?.length === 81 && (daily ? saved.id === root.dataset.id : !this.solvedWith(saved))) {
      this.load(saved.id, saved.puzzle, saved.solution, saved.entries, saved.notes, saved.past, saved.attempt, saved.cages)
      this.say("Welcome back. Your puzzle is where you left it.")
    } else {
      this.load(this.id, root.dataset.puzzle ?? "", root.dataset.solution ?? "", undefined, undefined, undefined, undefined, root.dataset.cages ? (JSON.parse(root.dataset.cages) as Cage[]) : undefined)
    }
    root.querySelector<HTMLElement>("[data-keypad]")?.removeAttribute("hidden")
    if (daily) root.querySelector<HTMLElement>('[data-act="new"]')?.remove()
    root.addEventListener("click", (e) => this.onClick(e))
    root.addEventListener("keydown", (e) => this.onKey(e))
    root.addEventListener("contextmenu", (e) => {
      const td = (e.target as HTMLElement).closest<HTMLElement>("td[data-i]")
      if (td) { e.preventDefault(); this.select(Number(td.dataset.i)); this.toggleNotesMode() }
    })
    this.render()
  }

  private solvedWith(s: Saved): boolean {
    return s.entries.every((v, i) => (s.puzzle[i] !== "0" ? true : v === Number(s.solution[i])))
  }

  private get ruleset(): string { return `${this.kind}:${this.level}` }

  private load(id: string, puzzle: string, solution: string, entries?: number[], notes?: number[], past?: Snapshot[], attempt = newAttempt(), cages?: Cage[]): void {
    this.id = id
    if (this.kind === "killer" && cages) this.applyCages(cages)
    this.attempt = attempt
    this.winSent = false
    this.givens = Array.from(puzzle, Number)
    this.solution = Array.from(solution, Number)
    this.entries = entries?.slice() ?? new Array(81).fill(0)
    this.notes = notes?.slice() ?? new Array(81).fill(0)
    this.past = past?.slice() ?? []
    this.future = []
    this.wrong.clear()
    this.cells.forEach((td, i) => {
      const g = this.givens[i]!
      td.classList.toggle("given", g > 0)
      td.classList.toggle("open", g === 0)
      if (g) td.setAttribute("aria-readonly", "true")
      else td.removeAttribute("aria-readonly")
    })
  }

  private value(i: number): number { return this.givens[i] || this.entries[i]! }

  /** Cage outlines (data-edges: which sides border another cage) and the sum on each cage's first square. */
  private applyCages(cages: Cage[]): void {
    this.cages = cages
    const { cage, sumAt, edges } = cageLayout(cages)
    this.cageOf = cage
    this.cells.forEach((td, i) => {
      td.dataset.edges = edges[i]!
      const sum = sumAt.get(i)
      if (sum !== undefined) td.dataset.sum = String(sum)
      else delete td.dataset.sum
    })
  }

  private cageText(i: number): string {
    const cg = this.cages[this.cageOf[i] ?? -1]
    return cg ? `, in a cage of ${cg.length - 1} squares that adds up to ${cg[0]}` : ""
  }

  // ---- edits -------------------------------------------------------------------------------
  private snapshot(): void {
    this.past.push({ entries: this.entries.slice(), notes: this.notes.slice(), attempt: this.attempt })
    if (this.past.length > 300) this.past.shift()
    this.future = []
  }

  enter(d: number, asNote = this.notesMode): void {
    const i = this.sel
    if (this.givens[i]) return this.say("That number is part of the puzzle. You can't change it.")
    if (asNote) {
      if (this.entries[i]) return this.say("Erase the number first to add notes here.")
      this.snapshot()
      this.notes[i] = this.notes[i]! ^ bit(d)
      this.commit(`Notes for row ${ROW(i) + 1}, column ${COL(i) + 1}: ${noteList(this.notes[i]!).join(" ") || "none"}.`)
      return
    }
    this.snapshot()
    this.entries[i] = this.entries[i] === d ? 0 : d
    this.notes[i] = 0
    if (this.entries[i]) for (const p of PEERS[i]!) this.notes[p] = this.notes[p]! & ~bit(d) // auto-prune peer notes
    const done = this.entries[i] ? this.completedUnits(i) : []
    this.commit(this.entries[i] ? `Row ${ROW(i) + 1}, column ${COL(i) + 1}: ${d}.${done.length ? ` ${done.join(" ")}` : ""}` : "Cleared.")
  }

  erase(): void {
    const i = this.sel
    if (this.givens[i] || (!this.entries[i] && !this.notes[i])) return
    this.snapshot()
    this.entries[i] = 0
    this.notes[i] = 0
    this.commit("Cleared.")
  }

  undo(): void {
    const p = this.past.pop()
    if (!p) return this.say("Nothing to undo.")
    this.undos++
    this.future.push({ entries: this.entries.slice(), notes: this.notes.slice(), attempt: this.attempt })
    this.entries = p.entries; this.notes = p.notes; this.attempt = p.attempt ?? this.attempt
    this.commit("Undone.")
  }

  redo(): void {
    const n = this.future.pop()
    if (!n) return this.say("Nothing to redo.")
    this.past.push({ entries: this.entries.slice(), notes: this.notes.slice(), attempt: this.attempt })
    this.entries = n.entries; this.notes = n.notes; this.attempt = n.attempt ?? this.attempt
    this.commit("Redone.")
  }

  check(): void {
    this.wrong = new Map(this.entries.flatMap((v, i) => (v && v !== this.solution[i] ? [[i, v] as [number, number]] : [])))
    this.render()
    const n = this.wrong.size
    this.say(n ? `${n === 1 ? "1 square doesn't" : `${n} squares don't`} match the solution. ${n === 1 ? "It has a dashed outline" : "They have dashed outlines"}.` : "No mistakes so far.")
  }

  hint(): void {
    let i = this.sel
    if (this.givens[i] || this.entries[i] === this.solution[i]) i = this.value(i) ? this.cells.findIndex((_, k) => !this.value(k)) : i
    if (i < 0) return this.say("Every square is filled. Choose Check to look for mistakes.")
    this.hints++
    this.sel = i
    this.snapshot()
    this.entries[i] = this.solution[i]!
    this.notes[i] = 0
    for (const p of PEERS[i]!) this.notes[p] = this.notes[p]! & ~bit(this.solution[i]!) // as when you type it
    this.commit(`Hint: row ${ROW(i) + 1}, column ${COL(i) + 1} is ${this.solution[i]}.`)
  }

  /** This puzzle again with only its given numbers. Undo brings the player's numbers and notes back. */
  restart(): void {
    if (!this.entries.some(Boolean) && !this.notes.some(Boolean)) return this.say("Nothing to clear yet.")
    if (this.sentFirst) gameEvent({ type: "end", ruleset: this.ruleset, outcome: "new_game", undos: this.undos, hints: this.hints, deal: this.id })
    this.sentFirst = false; this.undos = 0; this.hints = 0
    this.past.push({ entries: this.entries.slice(), notes: this.notes.slice(), attempt: this.attempt })
    this.future = []
    this.entries.fill(0)
    this.notes.fill(0)
    this.attempt = newAttempt()
    this.winSent = false
    this.commit("Restarted. Undo brings back your numbers.")
  }

  async newPuzzle(): Promise<void> {
    try {
      const res = await fetch(`/puzzles/${this.kind}/${this.level}.json`)
      const bank = (await res.json()) as { id: string; p: string; s: string; c?: Cage[] }[]
      const choices = bank.filter((b) => b.id !== this.id)
      const pick = choices[Math.floor(Math.random() * choices.length)]
      if (!pick) return
      if (this.sentFirst) gameEvent({ type: "end", ruleset: this.ruleset, outcome: "new_game", undos: this.undos, hints: this.hints, deal: this.id })
      this.sentFirst = false; this.undos = 0; this.hints = 0
      this.load(pick.id, pick.p, pick.s, undefined, undefined, undefined, undefined, pick.c)
      this.sel = 0
      this.commit("New puzzle ready.")
      this.cells[this.firstOpen()]?.focus()
    } catch {
      this.say("Couldn't load a new puzzle. Check your connection and try again.")
    }
  }

  private toggleNotesMode(): void {
    this.notesMode = !this.notesMode
    this.root.querySelector('[data-act="notes"]')?.setAttribute("aria-pressed", String(this.notesMode))
    this.say(this.notesMode ? "Notes on. The numbers you choose go in as notes." : "Notes off.")
  }

  private completedUnits(i: number): string[] {
    const out: string[] = []
    const names = [`Row ${ROW(i) + 1} complete.`, `Column ${COL(i) + 1} complete.`, `Box ${BOX(i) + 1} complete.`]
    ;[UNITS[ROW(i)]!, UNITS[9 + COL(i)]!, UNITS[18 + BOX(i)]!].forEach((u, k) => {
      if (u.every((j) => this.value(j) === this.solution[j])) out.push(names[k]!)
    })
    return out
  }

  private commit(message: string): void {
    for (const [i, d] of this.wrong) if (this.value(i) !== d) this.wrong.delete(i) // only changed squares lose their mark
    this.render()
    const won = this.cells.every((_, i) => this.value(i) === this.solution[i])
    writeJSON(this.saveKey, {
      v: 1, id: this.id, puzzle: this.givens.join(""), solution: this.solution.join(""),
      entries: this.entries, notes: this.notes, past: this.past.slice(-60), attempt: this.attempt,
      ...(this.cages.length ? { cages: this.cages } : {}),
    } satisfies Saved)
    const ruleset = this.ruleset
    const realMove = message !== "New puzzle ready." && message !== "Undone." && message !== "Redone." && !message.startsWith("Restarted")
    if (realMove && !this.sentFirst) { this.sentFirst = true; gameEvent({ type: "first_move", ruleset, daily: !!this.daily }) }
    if (won && realMove && !this.winSent) { this.winSent = true; gameEvent({ type: "end", ruleset, outcome: "win", undos: this.undos, hints: this.hints, deal: this.id, daily: !!this.daily }) }
    if (this.daily && message !== "New puzzle ready.") mark(this.daily.game, this.daily.date, won ? "won" : "played")
    this.say(won ? (this.daily ? (isToday(this.daily.date) ? "You solved today's puzzle! Come back tomorrow for a new one." : `You solved the puzzle for ${formatLong(this.daily.date)}!`) : "You solved the puzzle! Choose New puzzle to play again.") : message)
  }

  // ---- input ---------------------------------------------------------------------------------
  private select(i: number, focus = true): void {
    this.sel = Math.max(0, Math.min(80, i))
    this.render()
    if (focus) this.cells[this.sel]?.focus()
  }

  private firstOpen(): number {
    const i = this.cells.findIndex((_, k) => !this.value(k))
    return i < 0 ? 0 : i
  }

  private onClick(e: Event): void {
    const t = e.target as HTMLElement
    const td = t.closest<HTMLElement>("td[data-i]")
    if (td) return this.select(Number(td.dataset.i))
    const digit = t.closest<HTMLElement>("[data-digit]")
    if (digit) { this.enter(Number(digit.dataset.digit)); return }
    switch (t.closest<HTMLElement>("[data-act]")?.dataset.act) {
      case "notes": return this.toggleNotesMode()
      case "erase": return this.erase()
      case "undo": return this.undo()
      case "redo": return this.redo()
      case "check": return this.check()
      case "hint": return this.hint()
      case "new": void this.newPuzzle(); return
      case "restart": this.restart(); return
    }
  }

  private onKey(e: KeyboardEvent): void {
    if (!(e.target as HTMLElement).closest("td[data-i]")) {
      const mod = e.ctrlKey || e.metaKey
      if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? this.redo() : this.undo() }
      return
    }
    const mod = e.ctrlKey || e.metaKey
    const r = ROW(this.sel), c = COL(this.sel)
    if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); return e.shiftKey ? this.redo() : this.undo() }
    if (mod && e.key.toLowerCase() === "y") { e.preventDefault(); return this.redo() }
    const digit = /^Digit([1-9])$/.exec(e.code)?.[1] ?? (/^[1-9]$/.test(e.key) ? e.key : null)
    if (digit) { e.preventDefault(); return this.enter(Number(digit), this.notesMode || e.shiftKey) }
    switch (e.key) {
      case "ArrowLeft": e.preventDefault(); return this.select(r * 9 + Math.max(0, c - 1))
      case "ArrowRight": e.preventDefault(); return this.select(r * 9 + Math.min(8, c + 1))
      case "ArrowUp": e.preventDefault(); return this.select(Math.max(0, r - 1) * 9 + c)
      case "ArrowDown": e.preventDefault(); return this.select(Math.min(8, r + 1) * 9 + c)
      case "Home": e.preventDefault(); return this.select(mod ? 0 : r * 9)
      case "End": e.preventDefault(); return this.select(mod ? 80 : r * 9 + 8)
      case "Backspace": case "Delete": case "0": e.preventDefault(); return this.erase()
      case "n": case "N": return this.toggleNotesMode()
    }
  }

  // ---- render --------------------------------------------------------------------------------
  private say(msg: string): void { if (this.status) this.status.textContent = msg }

  private conflicts(): Set<number> {
    const out = new Set<number>()
    for (const u of UNITS) {
      const seen = new Map<number, number[]>()
      for (const i of u) { const v = this.value(i); if (v) seen.set(v, [...(seen.get(v) ?? []), i]) }
      for (const list of seen.values()) if (list.length > 1) list.forEach((i) => { if (!this.givens[i]) out.add(i) })
    }
    // Killer: a digit repeated in a cage, or a full cage that doesn't add up to its sum.
    for (const cg of this.cages) {
      const cells = cg.slice(1)
      const seen = new Map<number, number[]>()
      for (const i of cells) { const v = this.value(i); if (v) seen.set(v, [...(seen.get(v) ?? []), i]) }
      for (const list of seen.values()) if (list.length > 1) list.forEach((i) => { if (!this.givens[i]) out.add(i) })
      if (cells.every((i) => this.value(i)) && cells.reduce((n, i) => n + this.value(i), 0) !== cg[0]) cells.forEach((i) => { if (!this.givens[i]) out.add(i) })
    }
    return out
  }

  render(): void {
    gameProgress({ key: this.saveKey, ruleset: this.ruleset, id: `${this.id}#${this.attempt}`, started: this.entries.some(Boolean) || this.notes.some(Boolean), won: this.givens.every((g, i) => g > 0 || this.entries[i] === this.solution[i]) })
    const conflicts = this.conflicts()
    const selVal = this.value(this.sel)
    const peers = new Set(PEERS[this.sel])
    this.cells.forEach((td, i) => {
      const v = this.value(i)
      const n = this.notes[i]!
      td.tabIndex = i === this.sel ? 0 : -1
      td.setAttribute("aria-selected", String(i === this.sel))
      td.classList.toggle("is-selected", i === this.sel)
      td.classList.toggle("is-peer", peers.has(i))
      td.classList.toggle("is-same", !!selVal && v === selVal && i !== this.sel)
      td.classList.toggle("is-conflict", conflicts.has(i))
      td.classList.toggle("is-wrong", this.wrong.has(i))
      td.classList.toggle("entry", !this.givens[i] && !!v)
      if (v) td.textContent = String(v)
      else if (n) td.innerHTML = `<span class="notes" aria-hidden="true">${[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => `<span>${n & bit(d) ? d : ""}</span>`).join("")}</span>`
      else td.textContent = ""
      const state = this.givens[i] ? `${v}, given` : v ? `${v}${conflicts.has(i) ? ", conflicts with another square" : ""}${this.wrong.has(i) ? ", doesn't match the solution" : ""}` : `empty${n ? `, notes ${noteList(n).join(" ")}` : ""}`
      td.setAttribute("aria-label", `Row ${ROW(i) + 1}, column ${COL(i) + 1}, ${state}${this.cageText(i)}`)
    })
  }
}
